import * as XLSX from "xlsx";
import type { ColumnMapping, MappedRow } from "@/types/backtest";

// Column name patterns for auto-detection (order matters — more specific first)
const COLUMN_PATTERNS: Record<keyof ColumnMapping, RegExp[]> = {
  invoiceNumber: [
    /invoice\s*(?:#|num|number|no\.?)/i,
    /inv\s*(?:#|num|no\.?)/i,
    /^inv\.?$/i,
    /^reference$/i,
    /^ref\.?$/i,
  ],
  supplierName: [
    /vendor\s*name/i,
    /supplier\s*name/i,
    /payee\s*name/i,
    /^vendor$/i,
    /^supplier$/i,
    /^payee$/i,
    /^company$/i,
  ],
  invoiceDate: [
    /invoice\s*date/i,
    /inv\.?\s*date/i,
    /date\s*of\s*invoice/i,
    /bill\s*date/i,
    /^date$/i,
  ],
  invoiceAmount: [
    /invoice\s*(?:amount|total)/i,
    /amount\s*(?:due|billed)?/i,
    /(?:invoice\s*)?total/i,
    /balance\s*due/i,
    /^amount$/i,
  ],
  paymentTerms: [
    /payment\s*terms/i,
    /pay\s*terms/i,
    /terms\s*(?:of\s*payment)?/i,
    /discount\s*terms/i,
    /^terms$/i,
  ],
  actualPaymentDate: [
    /(?:actual\s*)?payment\s*date/i,
    /date\s*paid/i,
    /paid\s*date/i,
    /check\s*date/i,
    /payment\s*cleared/i,
    /cleared\s*date/i,
  ],
};

export function detectColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    supplierName: null,
    invoiceDate: null,
    invoiceAmount: null,
    paymentTerms: null,
    actualPaymentDate: null,
    invoiceNumber: null,
  };

  for (const [field, patterns] of Object.entries(COLUMN_PATTERNS) as [
    keyof ColumnMapping,
    RegExp[],
  ][]) {
    for (const pattern of patterns) {
      const match = headers.find((h) => pattern.test(h));
      if (match && !mapping[field]) {
        mapping[field] = match;
        break;
      }
    }
  }

  return mapping;
}

/** RFC 4180-compliant CSV parser */
export function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const nonEmpty = lines.filter((l) => l.trim().length > 0);
  if (nonEmpty.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        cells.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    return cells;
  };

  const headers = parseLine(nonEmpty[0]).map((h) => h.replace(/^"|"$/g, "").trim());

  const rows = nonEmpty.slice(1).map((line) => {
    const values = parseLine(line);
    return Object.fromEntries(
      headers.map((h, i) => [h, (values[i] ?? "").replace(/^"|"$/g, "").trim()])
    );
  });

  return { headers, rows };
}

/**
 * Parse an Excel workbook (XLSX/XLS) buffer into the same shape as parseCSV.
 * Uses the first sheet.
 */
export function parseXLSX(buffer: ArrayBuffer): {
  headers: string[];
  rows: Record<string, string>[];
} {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { headers: [], rows: [] };

  const sheet = workbook.Sheets[sheetName];
  const raw: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  if (raw.length === 0) return { headers: [], rows: [] };

  const headers = (raw[0] as unknown[]).map((h) => String(h ?? "").trim());

  const rows = (raw.slice(1) as unknown[][])
    .filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""))
    .map((row) => {
      return Object.fromEntries(
        headers.map((h, i) => {
          const cell = row[i];
          // Format dates from XLSX as YYYY-MM-DD
          if (cell instanceof Date) {
            return [h, cell.toISOString().slice(0, 10)];
          }
          return [h, String(cell ?? "").trim()];
        })
      );
    });

  return { headers, rows };
}

export function mapRows(rows: Record<string, string>[], mapping: ColumnMapping): MappedRow[] {
  return rows.map((row, i) => {
    const amountRaw = mapping.invoiceAmount ? (row[mapping.invoiceAmount] ?? "") : "";
    const amount = parseFloat(amountRaw.replace(/[$,\s]/g, ""));

    return {
      rowNumber: i + 2, // 1-indexed, row 1 is header
      invoiceNumber: mapping.invoiceNumber ? row[mapping.invoiceNumber] || null : null,
      supplierName: mapping.supplierName ? row[mapping.supplierName] || null : null,
      invoiceDate: mapping.invoiceDate ? row[mapping.invoiceDate] || null : null,
      invoiceAmount: isNaN(amount) ? null : amount,
      paymentTerms: mapping.paymentTerms ? row[mapping.paymentTerms] || null : null,
      actualPaymentDate: mapping.actualPaymentDate ? row[mapping.actualPaymentDate] || null : null,
    };
  });
}
